import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { entrepotActions } from "../../sagas/entrepotSlice";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Spinner,
    Table,
    Badge,
    InputGroup,
    InputGroupText,
    Input
} from "reactstrap";
import { FaCubes, FaFileExcel, FaFileUpload, FaSearch, FaTimes } from "react-icons/fa";
import Select from "react-select";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { stockActions } from "../../sagas/stockSlice";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const StockPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { stocks, loading: stockLoading } = useSelector((state) => state.stock);

    const fileInputRef = useRef(null);

    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const [selectedEntrepot, setSelectedEntrepot] = useState(null);

    const [familleFilter, setFamilleFilter] = useState("ALL");

    // Load entrepôts + stocks on mount
    useEffect(() => {
        dispatch(stockActions.listStocks());
    }, []);

    // Debounce search input
    useEffect(() => {
        const id = setTimeout(() => setSearchTerm(searchInput.trim()), 200);
        return () => clearTimeout(id);
    }, [searchInput]);

    const matches = useCallback((value, q) => {
        if (!q) return true;
        if (!value) return false;
        return String(value).toLowerCase().includes(q.toLowerCase());
    }, []);

    const refrechStocks = () => {
        dispatch(
            stockActions.listStocks({
                entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
            })
        );
    };

    // File Upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
            ];

            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: "error",
                    title: t("product.invalidFileType"),
                    text: t("product.excelOnly"),
                });
                return;
            }

            try {
                const formData = new FormData();
                formData.append("file", file);

                dispatch(
                    stockActions.uploadStocks({
                        formData,
                        onSuccess: () => {
                            refrechStocks();
                            Swal.fire({
                                icon: "success",
                                title: t("stock.uploadSuccess"),
                                confirmButtonText: "OK",
                            });
                        },
                    })
                );
            } catch (error) {
                console.error("Error parsing Excel:", error);
                Swal.fire({
                    icon: "error",
                    title: "Erreur!",
                    text: "Erreur lors de l'importation des produits",
                });
            } finally {
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        }
    };

    const filteredStocks = useMemo(() => {
        if (!stocks) return [];
        console.log(familleFilter)
        return stocks
            .filter((s) => {
                // FAMILLE
                if (familleFilter !== "ALL") {
                    if (!s.articleFamille) return false;
                    if (String(s.articleFamille).toUpperCase() !== familleFilter.toUpperCase())
                        return false;
                }
                return true;
            })
            .filter((s) => {
                // SEARCH
                if (!searchTerm) return true;
                return (
                    matches(s.articleNom, searchTerm) ||
                    matches(s.entrepotNom, searchTerm)
                );
            });
    }, [stocks, familleFilter, searchTerm, matches]);


    // Excel Export
    const handleExportExcel = useCallback(() => {
        const now = new Date();
        const datePart = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");

        const HEAD_ENTREPOT = t("entrepot.libelle") || "Entrepôt";
        const HEAD_ARTICLE = t("product.description") || "Article";
        const HEAD_QTE = t("product.stockDisponible") || "Stock disponible";

        const data = filteredStocks.map((s) => ({
            [HEAD_ENTREPOT]: s.entrepotNom ?? "",
            [HEAD_ARTICLE]: s.articleNom ?? "",
            [HEAD_QTE]: s.quantiteDisponible ?? 0,
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stocks");

        const filename = `stocks_export_${datePart}.xlsx`;
        XLSX.writeFile(wb, filename);
    }, [filteredStocks, t]);

    const loading = stockLoading;

    return (
        <>
            {loading ? (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="mt-2 text-muted">{t("common.loading")}</div>
                </div>
            ) : (
                <Container fluid>
                    {/* Header */}
                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow-sm">
                                <CardBody className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        <FaCubes className="text-primary me-2" />
                                        {t("stock.title")}
                                    </h4>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Search + Filters */}
                    <Row className="mb-4">
                        <Col>
                            <div className="d-flex gap-2 align-items-stretch">
                                {/* Search */}
                                <div className="flex-grow-1">
                                    <InputGroup className="shadow-sm">
                                        <InputGroupText>
                                            <FaSearch />
                                        </InputGroupText>
                                        <Input
                                            type="text"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            placeholder={t("common.search")}
                                        />
                                        {searchInput && (
                                            <Button color="light" onClick={() => setSearchInput("")}>
                                                <FaTimes />
                                            </Button>
                                        )}
                                    </InputGroup>
                                </div>

                                <div style={{ width: "180px" }}>
                                    <Input
                                        type="select"
                                        value={familleFilter}
                                        onChange={(e) => setFamilleFilter(e.target.value)}
                                    >
                                        <option value="ALL">Tous</option>
                                        <option value="COSMETIQUE">COSMÉTIQUE</option>
                                        <option value="MOXOM">MOXOM</option>
                                    </Input>
                                </div>

                                {/* Export + Upload */}
                                <div className="d-flex mx-3">
                                    <Button
                                        size="sm"
                                        color="success"
                                        onClick={handleExportExcel}
                                        disabled={!filteredStocks.length}
                                        className="shadow-sm d-flex align-items-center"
                                    >
                                        <FaFileExcel className="me-2" />
                                        {t("common.exportExcel")}
                                    </Button>

                                    <Button
                                        size="sm"
                                        color="primary"
                                        className="mx-2"
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <FaFileUpload className="me-2" size={12} />
                                        {t("stock.upload")}
                                    </Button>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                        accept=".xlsx,.xls"
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Stocks Table */}
                    <Row>
                        <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t("entrepot.libelle")}</th>
                                        <th>{t("product.description")}</th>
                                        <th className="text-end">{t("product.stockDisponible")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!filteredStocks.length ? (
                                        <tr>
                                            <td colSpan="3" className="text-center py-4">
                                                <div className="text-muted">{t("stock.noStocks")}</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredStocks.map((stock) => (
                                            <tr key={stock.id}>
                                                <td>{stock.entrepotNom}</td>
                                                <td>{stock.articleNom}</td>
                                                <td className="text-end">{stock.quantiteDisponible}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Row>
                </Container>
            )}
        </>
    );
};

export default StockPage;
